import { COORDINATE_SYSTEM, Layer } from '@deck.gl/core';
import { PathLayer } from '@deck.gl/layers';
import * as turf from '@turf/turf';
import { IFlightItem, LngLatArray } from '../flights/types';
import { RangeType } from '../RangeType';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { AnimatedVectorLineLayer } from './layers/vector/animated-vector-line-layer';
import { VectorLineLayer } from './layers/vector/vector-line-layer';

export class DeckVectors extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    createLayers(data: IFlightItem[]): Layer[] {
        if (!this.props?.flights.active.visible) {
            return [];
        }

        const { range, type, lineColor } = this.props.flights.active.vectors;
        const vectorLayerProps: Partial<VectorLineLayer<IFlightItem>['props']> = {
            data,
            pickable: false,
            coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
            wrapLongitude: true,
            rangeType: type,
            range: type === RangeType.time ? range / 60 : range,
            getColor: lineColor,
            getWidth: 1,
            getSourcePosition: (d) => this.getFlightPosition(d),
            getSpeed: (d) => this.getFlightSpeed(d),
            getHeading: (d) => this.getFlightHeading(d) ?? 0,
            getIsHidden: (d) => this.getIsHidden(d) || (this.getFlightHeading(d) === null ? 1 : 0),
            updateTriggers: {
                all: [this.flightsVersion],
                getIsHidden: this.getIsHiddenUpdateTrigger
            },
            parameters: this.plugin.getRenderingParams({
                cullMode: 'back'
            })
        };

        return [
            this.isCoastMode
                ? new AnimatedVectorLineLayer<IFlightItem>({
                      ...vectorLayerProps,
                      getId: (d) => d.id,
                      positionTexture: this.plugin.positionTexture,
                      id: 'animated-vectors'
                  })
                : this.useGlobeView
                  ? // LineLayer (and as a consequence VectorLineLayer) doesn't work properly in globe view.
                    // Workaround until VectorPathLayer is implemented.
                    new PathLayer<IFlightItem>({
                        id: 'globe-vectors',
                        data,
                        getWidth: 1,
                        widthUnits: 'pixels',
                        getColor: lineColor,
                        getPath: (d) => {
                            // FIXME: this is called every update! Expensive!
                            const currentPosition = this.getFlightPosition(d);
                            const speed = this.getFlightHeading(d) === null ? 0 : this.getFlightSpeed(d);
                            if (speed === 0) {
                                return [currentPosition, [...currentPosition]];
                            }

                            const distance = type === RangeType.time ? (speed * range) / 60 : range;
                            const endPosition = turf.destination(
                                this.getFlightPosition(d),
                                distance,
                                ((this.getFlightHeading(d) ?? 0) * 180) / Math.PI,
                                {
                                    units: 'nauticalmiles'
                                }
                            ).geometry.coordinates;
                            return [currentPosition, endPosition as LngLatArray];
                        },
                        parameters: this.plugin.getRenderingParams({
                            cullMode: 'back'
                        }),
                        updateTriggers: {
                            all: [this.flightsVersion, this.propsVersion]
                        }
                    })
                  : // Use LineLayer for mercator projection. It is more efficient (calculates end position in shaders)
                    new VectorLineLayer<IFlightItem>({
                        ...vectorLayerProps,
                        id: 'vectors'
                    })
        ];
    }
}
